/**
 * Data Aggregator — Sprint 16b
 *
 * Deterministic pre-aggregation of raw data before LLM processing.
 * Reduces tokens while preserving patterns. No LLM cost.
 *
 * Principle: purely structural — group, count, sort.
 * Zero semantic judgments (no "work vs personal", no "newsletter vs transactional").
 * All classification is done by the LLM in Pass 1.
 */
// ---------------------------------------------------------------------------
// Dynamic limits
// ---------------------------------------------------------------------------
/**
 * Calculate aggregation limits based on model context window.
 * Reserve 30% of context for pre-aggregated data.
 */
export function getAggregationLimits(modelContextTokens) {
    const dataBudget = Math.floor(modelContextTokens * 0.3);
    const perSource = Math.floor(dataBudget / 4);
    return {
        financial: Math.max(15, Math.floor(perSource / 40)),
        calendar: Math.max(15, Math.floor(perSource / 30)),
        email: Math.max(15, Math.floor(perSource / 30)),
        browsing: Math.max(15, Math.floor(perSource / 35)),
    };
}
// ---------------------------------------------------------------------------
// Recency boost
// ---------------------------------------------------------------------------
function recencyBoost(dateStr) {
    const daysSince = (Date.now() - new Date(dateStr).getTime()) / 86_400_000;
    if (daysSince < 7)
        return 2.0;
    if (daysSince < 30)
        return 1.5;
    return 1.0;
}
export function aggregateFinancial(transactions, limit) {
    if (transactions.length === 0)
        return '';
    // Group by merchant
    const byMerchant = new Map();
    for (const tx of transactions) {
        const key = tx.merchant.toLowerCase().trim();
        const existing = byMerchant.get(key) ?? [];
        existing.push(tx);
        byMerchant.set(key, existing);
    }
    // Aggregate per merchant
    const aggregates = [];
    for (const [, txs] of byMerchant) {
        const sorted = txs.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const totalSpend = txs.reduce((sum, t) => sum + Math.abs(t.amount), 0);
        const lastDate = sorted[sorted.length - 1].date;
        // Compound relevance score: spend × frequency × recency
        const score = Math.log(totalSpend + 1) * Math.log(txs.length + 1) * recencyBoost(lastDate);
        aggregates.push({
            merchant: txs[0].merchant,
            visitCount: txs.length,
            totalSpend: Math.round(totalSpend * 100) / 100,
            avgSpend: Math.round((totalSpend / txs.length) * 100) / 100,
            category: txs[0].category,
            firstDate: sorted[0].date,
            lastDate,
            isRecurring: txs.length >= 3,
            score,
        });
    }
    // Sort by compound score, take top N
    aggregates.sort((a, b) => b.score - a.score);
    const top = aggregates.slice(0, limit);
    return top
        .map(m => {
        const dateRange = m.firstDate.slice(0, 7) === m.lastDate.slice(0, 7)
            ? m.lastDate.slice(0, 7)
            : `${m.firstDate.slice(0, 7)}–${m.lastDate.slice(0, 7)}`;
        const recurring = m.isRecurring ? ', recurring' : '';
        const cat = m.category ? `, ${m.category}` : '';
        return `${m.merchant}: ${m.visitCount} visits, $${m.totalSpend} total (avg $${m.avgSpend})${cat}, ${dateRange}${recurring}`;
    })
        .join('\n');
}
export function aggregateCalendar(events, limit) {
    if (events.length === 0)
        return '';
    // Group recurring events by normalized title
    const groups = new Map();
    const oneOffs = [];
    for (const ev of events) {
        if (ev.isRecurring || (ev.recurrenceCount && ev.recurrenceCount > 1)) {
            const key = ev.title.toLowerCase().trim();
            const existing = groups.get(key);
            if (existing) {
                existing.occurrences += (ev.recurrenceCount ?? 1);
            }
            else {
                const hour = new Date(ev.startTime).getHours();
                const day = new Date(ev.startTime).getDay();
                groups.set(key, {
                    title: ev.title,
                    occurrences: ev.recurrenceCount ?? 1,
                    timeSlot: formatTimeSlot(hour),
                    dayType: day === 0 || day === 6 ? 'weekend' : 'weekday',
                    isCreator: ev.isCreator ?? false,
                    attendeeCount: ev.attendees?.length ?? 0,
                    location: ev.location,
                    isRecurring: true,
                });
            }
        }
        else {
            oneOffs.push(ev);
        }
    }
    const lines = [];
    // Recurring events
    for (const [, group] of groups) {
        const creator = group.isCreator ? 'self-created' : 'invited';
        const loc = group.location ? `, ${group.location}` : '';
        lines.push(`${group.title} (recurring, ${group.occurrences} occurrences, ${group.timeSlot}, ${group.dayType}, ${creator}, ${group.attendeeCount} attendees${loc})`);
    }
    // One-off events
    for (const ev of oneOffs) {
        const hour = new Date(ev.startTime).getHours();
        const day = new Date(ev.startTime).getDay();
        const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day];
        const attendees = ev.attendees?.length ?? 0;
        const loc = ev.location ? `, ${ev.location}` : '';
        lines.push(`${ev.title} (one-off, ${dayName} ${formatTimeSlot(hour)}, ${attendees} attendees${loc})`);
    }
    return lines.slice(0, limit).join('\n');
}
function formatTimeSlot(hour) {
    if (hour < 6)
        return 'early morning';
    if (hour < 12)
        return 'morning';
    if (hour < 14)
        return 'lunchtime';
    if (hour < 17)
        return 'afternoon';
    if (hour < 21)
        return 'evening';
    return 'night';
}
export function aggregateEmail(emails, limit) {
    if (emails.length === 0)
        return '';
    // Group by sender
    const bySender = new Map();
    for (const em of emails) {
        const key = em.sender.toLowerCase().trim();
        const existing = bySender.get(key);
        if (existing) {
            existing.emails.push(em);
        }
        else {
            bySender.set(key, { emails: [em], name: em.senderName });
        }
    }
    // Aggregate per sender
    const groups = [];
    for (const [sender, data] of bySender) {
        const subjects = data.emails
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .slice(0, 3)
            .map(e => e.subject);
        groups.push({
            sender: data.name ?? sender,
            senderName: data.name,
            count: data.emails.length,
            topSubjects: subjects,
        });
    }
    // Sort by count (most emails first) but ensure diversity:
    // Reserve 70% of slots for top senders by volume, 30% for unique-subject senders
    // to avoid high-volume newsletters drowning out restaurant bookings, events, etc.
    groups.sort((a, b) => b.count - a.count);
    const mainSlots = Math.ceil(limit * 0.7);
    const diversitySlots = limit - mainSlots;
    const mainGroup = groups.slice(0, mainSlots);
    const mainSubjectWords = new Set();
    for (const g of mainGroup) {
        for (const s of g.topSubjects) {
            for (const w of s.toLowerCase().split(/\s+/))
                mainSubjectWords.add(w);
        }
    }
    // Find senders NOT in main group whose subjects introduce new keywords
    const remaining = groups.slice(mainSlots);
    const diverse = [];
    for (const g of remaining) {
        if (diverse.length >= diversitySlots)
            break;
        const hasNovelSubject = g.topSubjects.some(s => {
            const words = s.toLowerCase().split(/\s+/);
            return words.some(w => w.length > 3 && !mainSubjectWords.has(w));
        });
        if (hasNovelSubject)
            diverse.push(g);
    }
    const selected = [...mainGroup, ...diverse];
    return selected
        .map(g => {
        const subjects = g.topSubjects.map(s => `"${s}"`).join('; ');
        return `${g.sender}: ${g.count} emails, subjects: ${subjects}`;
    })
        .join('\n');
}
// ---------------------------------------------------------------------------
// Email relationship extraction (bypass Pass 1 for person entities)
// ---------------------------------------------------------------------------
const AUTOMATED_SENDER_PATTERNS = [
    'noreply', 'no-reply', 'newsletter', 'notification', 'updates@',
    'support@', 'info@', 'mailer-daemon', 'donotreply',
];
/**
 * Extract a sender frequency table from raw emails for direct injection into Pass 2.
 * This bypasses Pass 1's lossy compression that drops relationship/person data.
 * Returns a formatted string of personal senders sorted by frequency, or '' if none found.
 */
export function aggregateEmailRelationships(emails) {
    if (emails.length === 0)
        return '';
    // Group by sender (same logic as aggregateEmail)
    const bySender = new Map();
    for (const em of emails) {
        const key = em.sender.toLowerCase().trim();
        const existing = bySender.get(key);
        if (existing) {
            existing.emails.push(em);
        }
        else {
            bySender.set(key, { emails: [em], name: em.senderName });
        }
    }
    const personalSenders = [];
    for (const [senderAddr, data] of bySender) {
        // Filter out single-email senders (likely automated or one-off)
        if (data.emails.length < 2)
            continue;
        // Filter out senders without a name (likely automated)
        if (!data.name || data.name.trim() === '')
            continue;
        // Filter out automated senders by address pattern
        const lowerAddr = senderAddr.toLowerCase();
        const isAutomated = AUTOMATED_SENDER_PATTERNS.some(p => lowerAddr.includes(p));
        if (isAutomated)
            continue;
        const recentSubjects = data.emails
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .slice(0, 3)
            .map(e => e.subject);
        personalSenders.push({
            name: data.name,
            count: data.emails.length,
            subjects: recentSubjects,
        });
    }
    if (personalSenders.length === 0)
        return '';
    // Sort by count descending, limit to top 15
    personalSenders.sort((a, b) => b.count - a.count);
    const top = personalSenders.slice(0, 15);
    return top
        .map(s => {
        const subjectList = s.subjects.map(subj => `"${subj}"`).join('; ');
        return `${s.name} (${s.count} emails): ${subjectList}`;
    })
        .join('\n');
}
// ---------------------------------------------------------------------------
// Browsing aggregation
// ---------------------------------------------------------------------------
export function aggregateBrowsing(entries, limit) {
    if (entries.length === 0)
        return '';
    // Group by domain
    const byDomain = new Map();
    for (const entry of entries) {
        let domain;
        let path;
        try {
            const url = new URL(entry.url);
            domain = url.hostname.replace(/^www\./, '');
            path = url.pathname;
        }
        catch {
            continue;
        }
        const existing = byDomain.get(domain);
        if (existing) {
            existing.visits += entry.visitCount;
            existing.totalDuration += entry.totalDuration ?? 0;
            const pathCount = existing.paths.get(path) ?? 0;
            existing.paths.set(path, pathCount + entry.visitCount);
        }
        else {
            const paths = new Map();
            paths.set(path, entry.visitCount);
            byDomain.set(domain, {
                visits: entry.visitCount,
                totalDuration: entry.totalDuration ?? 0,
                paths,
            });
        }
    }
    // Split into multi-visit and single-visit
    const multiVisit = [];
    const singleVisit = [];
    for (const [domain, data] of byDomain) {
        if (data.visits > 1) {
            const topPaths = [...data.paths.entries()]
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([p]) => p)
                .filter(p => p !== '/');
            multiVisit.push({
                domain,
                visits: data.visits,
                duration: Math.round(data.totalDuration / 60),
                topPaths,
            });
        }
        else {
            singleVisit.push(domain);
        }
    }
    // Sort multi-visit by visits descending
    multiVisit.sort((a, b) => b.visits - a.visits);
    const lines = [];
    // Multi-visit domains (detailed)
    const multiLimit = Math.max(5, Math.floor(limit * 0.7));
    for (const m of multiVisit.slice(0, multiLimit)) {
        const paths = m.topPaths.length > 0 ? ` — ${m.topPaths.join(', ')}` : '';
        const dur = m.duration > 0 ? `, ${m.duration}min total` : '';
        lines.push(`${m.domain}: ${m.visits} visits${dur}${paths}`);
    }
    // Single-visit domains (compact list)
    if (singleVisit.length > 0) {
        lines.push('');
        lines.push(`Single-visit domains: ${singleVisit.slice(0, 50).join(', ')}`);
    }
    return lines.join('\n');
}
// ---------------------------------------------------------------------------
// Main aggregation function
// ---------------------------------------------------------------------------
/**
 * Aggregate all source data for a detection pipeline run.
 * Returns per-source aggregated text strings.
 */
export function aggregateSourceData(data, modelContextTokens = 128_000) {
    const limits = getAggregationLimits(modelContextTokens);
    return {
        financial: aggregateFinancial(data.financial ?? [], limits.financial),
        calendar: aggregateCalendar(data.calendar ?? [], limits.calendar),
        email: aggregateEmail(data.email ?? [], limits.email),
        browsing: aggregateBrowsing(data.browsing ?? [], limits.browsing),
    };
}
//# sourceMappingURL=data-aggregator.js.map